import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";

type Kind = "planning" | "tracking";

type Expense = {
  id: string;
  kind: Kind;
  vendor: string;
  item: string | null;
  category: string | null;
  amount: string; // pg NUMERIC as string
  notes: string | null;
  incurred_on: string | null;
  created_at: string;
};

type SortKey = "vendor" | "item" | "category" | "amount" | "incurred_on";

type EditingExpense = Expense | null;

function useExpenses() {
  const [items, setItems] = useState<Expense[]>([]);
  const load = () =>
    api
      .get<{ expenses: Expense[] }>("/api/admin/expenses")
      .then((r) => setItems(r.expenses));
  useEffect(() => {
    void load();
  }, []);
  return { items, reload: load };
}

export default function Expenses() {
  const [tab, setTab] = useState<Kind>("planning");
  const { items, reload } = useExpenses();
  const [sortKey, setSortKey] = useState<SortKey>("vendor");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [editing, setEditing] = useState<EditingExpense>(null);

  const filtered = useMemo(
    () => items.filter((e) => e.kind === tab),
    [items, tab]
  );

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = (a[sortKey] ?? "") as any;
      const bv = (b[sortKey] ?? "") as any;
      if (sortKey === "amount") {
        return (Number(av) - Number(bv)) * (sortDir === "asc" ? 1 : -1);
      }
      return (
        String(av).localeCompare(String(bv)) * (sortDir === "asc" ? 1 : -1)
      );
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const total = useMemo(
    () => sorted.reduce((s, e) => s + Number(e.amount || 0), 0),
    [sorted]
  );

  function toggleSort(key: SortKey) {
    if (key === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  return (
    <div className="expenses admin">
      <header className="admin-header">
        <h1 className="admin-heading">Expenses</h1>
      </header>

      <div className="expense-tabs">
        <button
          className={tab === "planning" ? "tab active" : "tab"}
          onClick={() => setTab("planning")}
        >
          Planning
        </button>
        <button
          className={tab === "tracking" ? "tab active" : "tab"}
          onClick={() => setTab("tracking")}
        >
          Tracking
        </button>
      </div>

      <NewExpenseForm kind={tab} onSaved={reload} />

      {editing && (
        <EditExpenseModal
          expense={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}

      <p className="expense-total">
        {tab === "planning" ? "Planned" : "Spent"} total: $
        {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
      </p>

      <div className="expense-table-scroll">
      <table className="expense-table">
        <thead>
          <tr>
            {(
              [
                ["vendor", "Vendor"],
                ["item", "Item"],
                ["category", "Category"],
                ["amount", "Amount"],
                ["incurred_on", "Date"],
              ] as [SortKey, string][]
            ).map(([k, label]) => (
              <th
                key={k}
                onClick={() => toggleSort(k)}
                className="sortable"
                aria-sort={
                  sortKey === k
                    ? sortDir === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
              >
                {label}
                {sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
              </th>
            ))}
            <th>Notes</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((e) => (
            <tr key={e.id}>
              <td>{e.vendor}</td>
              <td>{e.item ?? ""}</td>
              <td>{e.category ?? ""}</td>
              <td>
                ${Number(e.amount).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </td>
              <td>{e.incurred_on ?? ""}</td>
              <td>{e.notes ?? ""}</td>
              <td className="action-buttons">
                <button className="edit" onClick={() => setEditing(e)}>
                  Edit
                </button>
                <button
                  className="delete"
                  onClick={async () => {
                    if (!window.confirm("Delete this entry?")) return;
                    await api.del(`/api/admin/expenses/${e.id}`);
                    await reload();
                  }}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr>
              <td colSpan={7} className="muted">
                No entries yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
      <p className="muted">
        Entries are not final — feel free to edit or delete as plans change.
      </p>
    </div>
  );
}

function NewExpenseForm({
  kind,
  onSaved,
}: {
  kind: Kind;
  onSaved: () => void;
}) {
  const [vendor, setVendor] = useState("");
  const [item, setItem] = useState("");
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [incurredOn, setIncurredOn] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/admin/expenses", {
        kind,
        vendor,
        item,
        category,
        amount: Number(amount) || 0,
        notes,
        incurredOn: incurredOn || null,
      });
      setVendor("");
      setItem("");
      setCategory("");
      setAmount("");
      setNotes("");
      setIncurredOn("");
      onSaved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="admin-form">
      <label>
        Vendor
        <input
          required
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
        />
      </label>
      <label>
        Item
        <input value={item} onChange={(e) => setItem(e.target.value)} />
      </label>
      <label>
        Category
        <input
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        />
      </label>
      <label>
        Amount
        <input
          type="number"
          step="0.01"
          min="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </label>
      <label>
        {kind === "planning" ? "Estimated for" : "Paid on"}
        <input
          type="date"
          value={incurredOn}
          onChange={(e) => setIncurredOn(e.target.value)}
        />
      </label>
      <label className="wide">
        Notes
        <input value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <div className="form-actions">
        <button className="button" type="submit" disabled={busy}>
          Add {kind === "planning" ? "planned item" : "expense"}
        </button>
      </div>
    </form>
  );
}

function EditExpenseModal({
  expense,
  onClose,
  onSaved,
}: {
  expense: Expense;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [kind, setKind] = useState<Kind>(expense.kind);
  const [vendor, setVendor] = useState(expense.vendor);
  const [item, setItem] = useState(expense.item ?? "");
  const [category, setCategory] = useState(expense.category ?? "");
  const [amount, setAmount] = useState(expense.amount);
  const [notes, setNotes] = useState(expense.notes ?? "");
  const [incurredOn, setIncurredOn] = useState(expense.incurred_on ?? "");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/api/admin/expenses/${expense.id}`, {
        kind,
        vendor,
        item,
        category,
        amount: Number(amount) || 0,
        notes,
        incurredOn: incurredOn || null,
      });
      onSaved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Expense</h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <form onSubmit={onSubmit} className="admin-form">
          <label>
            Type
            <select value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
              <option value="planning">Planning</option>
              <option value="tracking">Tracking</option>
            </select>
          </label>
          <label>
            Vendor
            <input
              required
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
            />
          </label>
          <label>
            Item
            <input value={item} onChange={(e) => setItem(e.target.value)} />
          </label>
          <label>
            Category
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
          </label>
          <label>
            Amount
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          <label>
            {kind === "planning" ? "Estimated for" : "Paid on"}
            <input
              type="date"
              value={incurredOn}
              onChange={(e) => setIncurredOn(e.target.value)}
            />
          </label>
          <label className="wide">
            Notes
            <input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
          <div className="form-actions">
            <button type="button" className="button secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="button" type="submit" disabled={busy}>
              Save changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
